import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Printer, Sparkles, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import {
  school, kpis, hdiHeatmap, careerInterests, futureSkills, studios, aiInsightsPrincipal,
} from "@/data/greenwood";
import { cn } from "@/lib/utils";

function heatColor(v: number) {
  if (v >= 85) return "bg-primary text-primary-foreground";
  if (v >= 78) return "bg-primary/70 text-primary-foreground";
  if (v >= 70) return "bg-primary/40";
  if (v >= 62) return "bg-primary/25";
  return "bg-primary/10";
}

const maxInterest = Math.max(...careerInterests.map((c) => c.value));

/**
 * Board-ready single-page snapshot of the school. Optimised for print & PDF.
 */
export default function Boardpack() {
  return (
    <>
      <PageHead title={`${school.name} | Board Pack`} description="Board-ready school snapshot" path="/schools/boardpack" />
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          html, body { background: white !important; }
          .no-print, [data-sidebar], header.sticky { display: none !important; }
          .print-break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
          .print-break-before { break-before: page; }
          main { padding: 0 !important; }
          .container { max-width: 100% !important; padding: 0 !important; }
          .print-grid-2 { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 8mm !important; }
          .print-grid-4 { display: grid !important; grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: 4mm !important; }
          * { box-shadow: none !important; backdrop-filter: none !important; }
          a { color: inherit !important; text-decoration: none !important; }
        }
      `}</style>

      <div className="container max-w-6xl py-8 space-y-8 print:py-0 print:space-y-6">
        <div className="no-print flex items-center justify-between gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/schools"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Principal</Link>
          </Button>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="rounded-full">
              Board Pack · {new Date().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
            </Badge>
            <Button size="sm" onClick={() => window.print()}>
              <Printer className="w-3.5 h-3.5 mr-1" /> Print / Save as PDF
            </Button>
          </div>
        </div>

        <section className="print-break-inside-avoid rounded-2xl border bg-gradient-to-br from-card via-card to-primary/5 p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
                <Sparkles className="w-3.5 h-3.5" /> WeKIT Education Intelligence™ · Board Snapshot
              </div>
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{school.name}</h1>
              <p className="text-muted-foreground mt-1">
                {school.principal} · Academic Year {school.academicYear} · {school.location}
              </p>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <div>{school.students.toLocaleString()} students</div>
              <div>{school.teachers} teachers · {school.counsellors} counsellors</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 print-grid-4">
            <BoardKpi label="Active students" value={kpis.activeStudents.toLocaleString()} />
            <BoardKpi label="Assessment completion" value={`${kpis.assessmentCompletion}%`} />
            <BoardKpi label="Human Development Index™" value={String(kpis.humanDevelopmentIndex)} />
            <BoardKpi label="Future Readiness Index™" value={String(kpis.futureReadinessIndex)} />
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-4 print-break-inside-avoid print-grid-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Human Development Heatmap</CardTitle>
              <p className="text-xs text-muted-foreground">Dimension × grade · higher is stronger</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="p-2 text-left font-medium text-muted-foreground">Dimension</th>
                      {hdiHeatmap.grades.map((g) => (
                        <th key={g} className="p-2 text-center font-medium text-muted-foreground">G{g}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {hdiHeatmap.dimensions.map((dim, i) => (
                      <tr key={dim}>
                        <td className="p-2 font-medium">{dim}</td>
                        {hdiHeatmap.values[i].map((v, j) => (
                          <td key={j} className="p-1.5">
                            <div className={cn("h-8 rounded-lg grid place-items-center text-xs font-semibold", heatColor(v))}>{v}</div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Career Interest Distribution</CardTitle>
              <p className="text-xs text-muted-foreground">Share of students exploring each cluster</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {careerInterests.map((c) => (
                <div key={c.cluster} className="grid grid-cols-[1fr_auto] items-center gap-3">
                  <div>
                    <div className="text-sm font-medium">{c.cluster}</div>
                    <div className="h-1.5 mt-1 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${(c.value / maxInterest) * 100}%` }} />
                    </div>
                  </div>
                  <span className="text-sm tabular-nums text-muted-foreground w-10 text-right">{c.value}%</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        <section className="grid md:grid-cols-2 gap-4 print-break-inside-avoid print-grid-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Future Skills</CardTitle>
              <p className="text-xs text-muted-foreground">School-wide FutureReadySkills™</p>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-3">
              {futureSkills.map((s) => (
                <div key={s.skill} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{s.skill}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {s.score}<span className="ml-1 text-emerald-600 text-xs">+{s.delta}</span>
                    </span>
                  </div>
                  <Progress value={s.score} className="h-1.5" />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Learning Studio Adoption</CardTitle>
              <p className="text-xs text-muted-foreground">Active learners across WeKIT studios</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {studios.map((s) => (
                <div key={s.name} className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                    <span className="text-[10px] font-bold">{s.name}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium truncate">{s.full}</div>
                      <div className="text-sm tabular-nums font-semibold">{s.adoption}%</div>
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">{s.active.toLocaleString()} active · {s.modules} modules</div>
                    <Progress value={s.adoption} className="h-1.5" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        <section className="print-break-inside-avoid">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> AI Insights · This week
              </CardTitle>
              <p className="text-xs text-muted-foreground">Evidence-based signals for the board</p>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              {aiInsightsPrincipal.slice(0, 4).map((i, idx) => (
                <div key={idx} className="rounded-xl border bg-muted/30 p-3">
                  <div className="text-sm font-medium">{i.title}</div>
                  <p className="text-xs text-muted-foreground mt-1">{i.body}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        <footer className="text-[10px] text-muted-foreground text-center pt-4 border-t">
          Prepared by WeKIT Education Intelligence™ · Confidential · For {school.name} board of governors
        </footer>
      </div>
    </>
  );
}

function BoardKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-background p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tracking-tight tabular-nums mt-1">{value}</div>
    </div>
  );
}
